import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Trash2, KeyRound, Shield } from "lucide-react";

/** Developer API — API-key management + copy-once token reveal.
 *  Renders inside Company Settings as a full-width section (Enterprise-grade;
 *  hidden from Community / free tenants via server-side scopes on /admin/api-keys). */
export default function DeveloperApi() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState({ timesheets: true, payroll: true, employees: false });
  const [newToken, setNewToken] = useState(null); // { token, name, token_preview } — only shown once
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/api-keys"); setKeys(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim() || name.trim().length < 2) {
      toast.error("Give the key a descriptive name (min 2 chars)");
      return;
    }
    setBusy(true);
    try {
      const scopesArr = Object.entries(scopes).filter(([, v]) => v).map(([k]) => `${k}:read`);
      const { data } = await api.post("/admin/api-keys", { name: name.trim(), scopes: scopesArr });
      setNewToken(data);
      setName("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const revoke = async (id, keyName) => {
    if (!window.confirm(`Revoke API key "${keyName}"? Any integrations using it will start failing immediately.`)) return;
    try {
      await api.delete(`/admin/api-keys/${id}`);
      toast.success("API key revoked");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copy = (val) => { navigator.clipboard.writeText(val); toast.success("Copied to clipboard"); };

  return (
    <div className="hard-card p-6 mb-8" data-testid="developer-api-section">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><KeyRound className="w-4 h-4" /> § DEVELOPER API · WHITE-LABEL LICENSING</div>
          <h2 className="font-heading font-black text-2xl">API Keys</h2>
        </div>
        <div className="font-mono text-[10px] px-2 py-1 border-2 border-warn text-warn font-black uppercase tracking-widest">Enterprise-Grade</div>
      </div>
      <p className="font-mono text-xs text-muted-foreground mb-4">
        Mint a Bearer token to hit <code className="bg-secondary px-1">GET /api/v1/timesheets</code>, <code className="bg-secondary px-1">/api/v1/payroll/stubs</code>, and <code className="bg-secondary px-1">/api/v1/employees</code> from your own app. Every key is scoped to this company only.
      </p>

      {newToken && (
        <div className="border-4 border-good bg-good/10 p-4 mb-4" data-testid="new-token-reveal">
          <div className="font-heading font-black text-good text-lg mb-2">✓ API key created — copy it now, we can't show it again</div>
          <div className="flex items-center gap-2 mb-2">
            <code className="flex-1 font-mono text-xs bg-white border-2 border-ink p-2 break-all" data-testid="new-token-value">{newToken.token}</code>
            <button onClick={() => copy(newToken.token)} className="hard-btn px-3 py-2 font-mono uppercase text-[10px] tracking-widest inline-flex items-center gap-2" data-testid="copy-token-btn"><Copy className="w-3 h-3" /> Copy</button>
          </div>
          <button onClick={() => setNewToken(null)} className="border-2 border-ink px-3 py-1 font-mono uppercase text-[10px] tracking-widest hover:bg-ink hover:text-white">I've saved it — dismiss</button>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-3 items-end mb-4">
        <div>
          <label className="overline mb-1 block">Key name</label>
          <input data-testid="new-key-name" value={name} onChange={(e) => setName(e.target.value)} className="input w-full" placeholder="Franchisee A — Timesheet Sync" />
        </div>
        <div className="col-span-2 flex flex-wrap items-center gap-3 pb-2">
          <label className="flex items-center gap-2 font-mono text-xs cursor-pointer">
            <input type="checkbox" data-testid="scope-timesheets" checked={scopes.timesheets} onChange={(e) => setScopes({ ...scopes, timesheets: e.target.checked })} /> timesheets:read
          </label>
          <label className="flex items-center gap-2 font-mono text-xs cursor-pointer">
            <input type="checkbox" data-testid="scope-payroll" checked={scopes.payroll} onChange={(e) => setScopes({ ...scopes, payroll: e.target.checked })} /> payroll:read
          </label>
          <label className="flex items-center gap-2 font-mono text-xs cursor-pointer">
            <input type="checkbox" data-testid="scope-employees" checked={scopes.employees} onChange={(e) => setScopes({ ...scopes, employees: e.target.checked })} /> employees:read
          </label>
          <button onClick={create} disabled={busy} data-testid="create-key-btn" className="hard-btn px-4 py-2 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 ml-auto disabled:opacity-50">
            <Shield className="w-4 h-4" /> {busy ? "Generating…" : "Generate Key"}
          </button>
        </div>
      </div>

      <div className="border-2 border-ink">
        <div className="grid grid-cols-6 font-mono text-[10px] uppercase tracking-widest bg-ink text-white px-3 py-2">
          <div className="col-span-2">Name</div><div>Prefix</div><div>Scopes</div><div>Last used</div><div className="text-right">Actions</div>
        </div>
        {keys.length === 0 ? (
          <div className="p-4 text-center font-mono text-xs text-muted-foreground">No API keys yet.</div>
        ) : keys.map((k) => (
          <div key={k.id} className={`grid grid-cols-6 items-center px-3 py-2 border-t-2 border-ink font-mono text-xs ${k.active === false ? "opacity-40" : ""}`} data-testid={`api-key-row-${k.id}`}>
            <div className="col-span-2 truncate">{k.name}</div>
            <div className="font-mono text-[10px]">{k.token_preview}</div>
            <div className="text-[10px]">{(k.scopes || []).join(", ")}</div>
            <div className="text-[10px]">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "Never"}{k.call_count ? ` · ${k.call_count} calls` : ""}</div>
            <div className="text-right">
              {k.active !== false ? (
                <button onClick={() => revoke(k.id, k.name)} className="p-1 border-2 border-bad text-bad hover:bg-bad hover:text-white" data-testid={`revoke-key-${k.id}`}><Trash2 className="w-3 h-3" /></button>
              ) : <span className="font-mono text-[10px] text-bad">Revoked</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
